import Input from "components/v2/Input";
import { isDatadogSynthetics, isProdOrPreProd } from "containers/app/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { selectSelectedUserDetails } from "store/user/selectors";
import { setUserProperties } from "utils";

import { getOTP } from "../../../../store/common/action";
import { showOTPModal } from "../../../../store/common/slice";
import LocalStorage from "../../../../utils/LocalStorage";
import Loader from "../../Loaders/FullScreenLoader/Loader";
import { showNotification } from "../../notification/notification";
import ConfirmModal from "../ConfirmModal";

const OTP_TYPE = {
  GENERATE_OTP: "generateOtp",
  VERIFY_OTP: "verifyOtp",
};

const OTPModal = ({ showOTPModal, getOTP, userDetails }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", type: OTP_TYPE.GENERATE_OTP });
  const [formErrors, setFormErrors] = useState({});

  const onClose = useCallback(() => {
    try {
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  }, [navigate]);

  const onPrimaryButtonClick = useCallback(async () => {
    try {
      if (
        (!isProdOrPreProd() || isDatadogSynthetics()) &&
        formData.type === OTP_TYPE.VERIFY_OTP &&
        formData?.otp === "2468"
      ) {
        LocalStorage.setItem("otp", formData.otp);
        showNotification({ message: "Otp verified successfully", type: "success" });
        showOTPModal(false);

        return;
      }

      const { error, payload } = await getOTP({
        payload: formData,
      });
      const { status, message } = payload || {};
      if (formData.type === OTP_TYPE.GENERATE_OTP) {
        if (error || !status) {
          const errorMessage = message || "Invalid Email";

          setFormErrors({ email: errorMessage });
          showNotification({ message: errorMessage, type: "error" });
        } else {
          LocalStorage.setItem("email", formData.email);
          showNotification({ message, type: "success" });
          setFormData({ ...formData, type: OTP_TYPE.VERIFY_OTP });
        }
      } else {
        if (error || !status) {
          const errorMessage = message || "Invalid OTP";

          setFormErrors({ otp: errorMessage });
          showNotification({ message: errorMessage, type: "error" });
        } else {
          LocalStorage.setItem("otp", formData.otp);
          showNotification({ message, type: "success" });
          showOTPModal(false);
          setUserProperties(userDetails);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [formData, getOTP, showOTPModal, userDetails]);

  const autoVerifyOTP = useCallback(async () => {
    setIsLoading(true);
    try {
      const email = LocalStorage.getItem("email");
      const otp = LocalStorage.getItem("otp");
      if (email && otp) {
        if ((!isProdOrPreProd() || isDatadogSynthetics()) && otp === "2468") {
          showOTPModal(false);

          return;
        }

        const { error, payload } = await getOTP({
          payload: {
            email,
            otp,
            type: OTP_TYPE.VERIFY_OTP,
          },
        });
        const { status } = payload || {};
        if (error || !status) {
        } else {
          showOTPModal(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  }, [getOTP, showOTPModal]);

  const modalTitle = useMemo(() => {
    if (formData.type === OTP_TYPE.GENERATE_OTP) {
      return "Generate OTP";
    }

    return "Verify OTP";
  }, [formData.type]);

  useEffect(() => {
    autoVerifyOTP();
  }, [autoVerifyOTP]);

  const onChange = useCallback(
    (key) => (e) => {
      try {
        setFormData((prev) => ({ ...prev, [key]: e.target.value }));
        setFormErrors((prev) => ({ ...prev, [key]: "" }));
      } catch (error) {
        console.error(error);
      }
    },
    [],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!Object.values(formErrors).some((error) => error.length) && e.key === "Enter") {
        onPrimaryButtonClick();
      }
    },
    [formErrors, onPrimaryButtonClick],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ConfirmModal
      title={modalTitle}
      onClose={onClose}
      secondaryButtonText="Back"
      onPrimaryButtonClick={onPrimaryButtonClick}
      primaryButtonText="Submit"
    >
      {formData.type === OTP_TYPE.GENERATE_OTP ? (
        <Input
          required={true}
          label="Email"
          dataTestId="OTPModal-Email"
          value={formData.email}
          onChange={onChange("email")}
          error={formErrors.email}
          name="OTPModal-Input-Email"
          placeholder="Enter email"
          onKeyDown={onKeyDown}
          focused
        />
      ) : null}
      {formData.type === OTP_TYPE.VERIFY_OTP ? (
        <Input
          required={true}
          type="number"
          label="OTP"
          dataTestId="OTPModal-OTP"
          value={formData.otp}
          onChange={onChange("otp")}
          error={formErrors.otp}
          onKeyDown={onKeyDown}
          autoFocus
        />
      ) : null}
    </ConfirmModal>
  );
};

const mapStateToProps = createStructuredSelector({
  userDetails: selectSelectedUserDetails,
});

const mapDispatchToProps = {
  showOTPModal,
  getOTP,
};

export default connect(mapStateToProps, mapDispatchToProps)(OTPModal);
